import type { InputHTMLAttributes } from "react";
import { useId, useState } from "react";

type Props = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hint?: string;
};

/**
 * ラベル付きの入力欄。
 *
 * ★`type="password"` のときは**表示/非表示の切り替え**を出す（U2・デモのログイン画面が
 * 参照仕様）。入力が見えないと打ち間違いに気づけず、「メールアドレスまたはパスワードが
 * 正しくありません」を何度も踏む（連続5回でロックアウト＝FR-114）。切り替えは**この欄の中で
 * 完結**させる＝各画面が同じ実装を書き直さない。
 */
export function Field({ label, hint, ...rest }: Props) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const isPassword = rest.type === "password";
  const [visible, setVisible] = useState(false);

  return (
    <div style={{ marginBottom: "var(--kz-space-4)" }}>
      <label
        htmlFor={id}
        style={{
          display: "block",
          marginBottom: "var(--kz-space-1)",
          fontSize: "var(--kz-font-size-sm)",
          fontWeight: 600,
        }}
      >
        {label}
      </label>
      <div style={{ position: "relative" }}>
        <input
          {...rest}
          type={isPassword && visible ? "text" : rest.type}
          id={id}
          aria-describedby={hintId}
          style={{
            width: "100%",
            padding: isPassword ? "10px 44px 10px 12px" : "10px 12px",
            borderRadius: "var(--kz-radius-sm)",
            border: "1px solid var(--kz-color-border)",
            background: "var(--kz-color-surface)",
            color: "var(--kz-color-ink)",
            fontSize: "var(--kz-font-size-md)",
            fontFamily: "inherit",
            ...rest.style,
          }}
        />
        {isPassword && (
          <button
            type="button"
            // ★aria-label は**いまの状態でなく、押すと何が起きるか**を言う
            //   （読み上げで「パスワードを表示」＝押せば見える、と伝わる）
            aria-label={visible ? "パスワードを隠す" : "パスワードを表示"}
            aria-pressed={visible}
            data-testid={`${rest.name ?? "password"}-toggle`}
            onClick={() => setVisible((v) => !v)}
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              height: "100%",
              width: 44,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              border: "none",
              background: "none",
              color: "var(--kz-color-ink-muted)",
              cursor: "pointer",
              padding: 0,
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
              <circle cx="12" cy="12" r="3.2" />
              {visible && <path d="M4 20 20 4" />}
            </svg>
          </button>
        )}
      </div>
      {hint ? (
        <p
          id={hintId}
          style={{
            margin: "var(--kz-space-1) 0 0",
            fontSize: "var(--kz-font-size-sm)",
            color: "var(--kz-color-ink-muted)",
          }}
        >
          {hint}
        </p>
      ) : null}
    </div>
  );
}
